import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import { getPopularMovies, searchMovies } from "@/utils/movies";

const PAGE_SIZE = 12;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = query ? searchMovies(query) : [];
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const popular = getPopularMovies(4);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  const goPage = (p: number) => {
    setSearchParams({ q: query, page: String(p) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          影片搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          支持按影片名称、地区、导演、演员或剧情关键词查找。
        </p>
      </header>

      <form onSubmit={submitSearch} className="mt-5 flex w-full flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center rounded-md border border-background-300 bg-background-50 px-4">
          <i className="ri-search-line text-lg text-foreground-400" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入影片名称、地区或剧情关键词"
            className="h-12 w-full bg-transparent px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-2-line" />
          搜索
        </button>
      </form>

      {query && (
        <p className="mt-4 text-sm text-foreground-600">
          关键词 “<strong className="text-primary-700">{query}</strong>” 共找到{" "}
          <strong className="text-foreground-950">{results.length}</strong> 部影片
        </p>
      )}

      {!query ? (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            输入关键词，开始你的选片
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            不妨试试搜索「日本」「爱情」「悬疑」等关键词。
          </p>
        </div>
      ) : results.length === 0 ? (
        <div className="mt-6">
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              没有找到与「{query}」相关的影片
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，或直接浏览热门推荐内容。
            </p>
            <Link
              to="/category"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-grid-line" />
              浏览全部片库
            </Link>
          </div>

          <section className="mt-10">
            <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950">
              热门推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
              {popular.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </section>
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} showSummary />
            ))}
          </div>
          <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={goPage} />
        </>
      )}
    </div>
  );
}